import { useEffect, useState } from "react";
import { Download, TrendingUp } from "lucide-react";
import api, { eur } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

const MONTHS = {
  it: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

const Report = () => {
  const { t, lang } = useI18n();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/revenue", { params: { year } }).then(({ data }) => setData(data));
  }, [year]);

  const downloadCsv = async () => {
    const res = await api.get("/reports/revenue.csv", { params: { year }, responseType: "blob" });
    const disposition = res.headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = match ? match[1] : `invoices-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  const max = Math.max(...data.months.map((m) => m.invoiced), 1);
  const labels = MONTHS[lang] || MONTHS.it;

  return (
    <div className="space-y-6" data-testid="report-page">
      {data.currencies?.length > 1 && (
        <p className="text-xs text-slate-500" data-testid="report-multi-currency">
          {t("dash.multiCurrency")} {data.currencies.join(", ")}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {(data.years.includes(year) ? data.years : [year, ...data.years]).map((y) => (
            <button
              key={y}
              data-testid={`year-${y}`}
              onClick={() => setYear(y)}
              className={`font-mono-num rounded-md px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ${
                y === year ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
        <Button data-testid="export-csv" onClick={downloadCsv} variant="outline" className="gap-2">
          <Download className="h-4 w-4" /> {t("report.csv")}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card-flat relative overflow-hidden bg-slate-950 p-6 text-white grain">
          <p className="relative text-xs font-semibold uppercase tracking-wide text-slate-400">
            {t("report.invoiced")}
          </p>
          <p className="font-mono-num relative mt-3 text-3xl font-semibold" data-testid="total-invoiced">
            {eur(data.totals.invoiced)}
          </p>
          <p className="relative mt-2 text-xs text-slate-400">
            {data.totals.count} {t("docs.countInvoices")}
          </p>
        </div>
        <div className="card-flat p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("report.collected")}
          </p>
          <p className="font-mono-num mt-3 text-3xl font-semibold text-emerald-600" data-testid="total-collected">
            {eur(data.totals.collected)}
          </p>
        </div>
        <div className="card-flat p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("report.outstanding")}
          </p>
          <p className="font-mono-num mt-3 text-3xl font-semibold text-amber-600" data-testid="total-outstanding">
            {eur(data.totals.outstanding)}
          </p>
        </div>
      </div>

      <div className="card-flat p-6">
        <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
          <TrendingUp className="h-4 w-4 text-blue-600" /> {t("report.byMonth")}
        </h2>
        <div className="mt-6 flex items-end gap-2" data-testid="report-chart">
          {data.months.map((m, i) => (
            <div key={m.month} className="flex flex-1 flex-col items-center gap-2">
              <div className="flex h-40 w-full items-end gap-0.5">
                <div
                  className="rise flex-1 rounded-t bg-slate-900"
                  style={{ height: `${(m.invoiced / max) * 100}%`, animationDelay: `${i * 40}ms` }}
                  title={`${t("report.invoiced")}: ${eur(m.invoiced)}`}
                />
                <div
                  className="rise flex-1 rounded-t bg-emerald-500"
                  style={{ height: `${(m.collected / max) * 100}%`, animationDelay: `${i * 40}ms` }}
                  title={`${t("report.collected")}: ${eur(m.collected)}`}
                />
              </div>
              <span className="text-[11px] font-semibold text-slate-500">{labels[i]}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded bg-slate-900" /> {t("report.invoiced")}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded bg-emerald-500" /> {t("report.collected")}
          </span>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">{t("report.month")}</th>
              <th className="px-4 py-3 text-right">{t("docs.countInvoices")}</th>
              <th className="px-4 py-3 text-right">{t("report.invoiced")}</th>
              <th className="px-4 py-3 text-right">{t("report.collected")}</th>
              <th className="px-4 py-3 text-right">{t("report.outstanding")}</th>
            </tr>
          </thead>
          <tbody data-testid="report-table">
            {data.months.map((m, i) => (
              <tr key={m.month} className="border-b border-slate-100 transition-colors duration-200 hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium">{labels[i]}</td>
                <td className="font-mono-num px-4 py-2.5 text-right text-slate-600">{m.count}</td>
                <td className="font-mono-num px-4 py-2.5 text-right">{eur(m.invoiced)}</td>
                <td className="font-mono-num px-4 py-2.5 text-right text-emerald-700">{eur(m.collected)}</td>
                <td className="font-mono-num px-4 py-2.5 text-right text-amber-700">{eur(m.outstanding)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Report;
